import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  getSchedulerHistory,
  getSchedulerHistoryDetail,
  getSchedulerStats,
} from '../api';
import { SchedulerHistoryItem, SchedulerHistoryDetail } from '../api/schedules';
import { ScheduleTask } from '../types';
import { useSports } from '../hooks';
import { useAdminList } from '../hooks/useAdminList';
import { Pagination, Button, AutoStatusBadge, Modal } from './common';
import { toast } from 'react-toastify';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { User } from '../api/auth';

interface SchedulerManagerProps {
  currentUser: User;
}

const SchedulerManager: React.FC<SchedulerManagerProps> = ({ currentUser }) => {
  const isAdministrator = currentUser.role === 'administrator';
  const [subTab, setSubTab] = useState<'manage' | 'history'>('manage');
  const [schedules, setSchedules] = useState<ScheduleTask[]>([]);
  const [histories, setHistories] = useState<SchedulerHistoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState<{
    current_page: number;
    total_pages: number;
    page_size: number;
    total_items: number;
    has_next: boolean;
    has_prev: boolean;
  }>({
    current_page: 1,
    total_pages: 0,
    page_size: 10,
    total_items: 0,
    has_next: false,
    has_prev: false
  });
  const [loading, setLoading] = useState(false);

  // 종목 데이터 - useSports 훅 사용
  const { sports, sportOptions, getSportLabelByName } = useSports();

  // 관리자 목록 (administrator만 자동 조회)
  const { adminList } = useAdminList({ immediate: isAdministrator });
  const [filterAdminId, setFilterAdminId] = useState<number | null>(null);   // 조회 필터용
  const [createAdminId, setCreateAdminId] = useState<number | null>(null);  // 새 스케줄 담당자용
  const [historyAdminId, setHistoryAdminId] = useState<number | null>(null); // 내역 담당자 필터용

  // 내역 상세 모달
  const [selectedDetail, setSelectedDetail] = useState<SchedulerHistoryDetail | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestIdRef = useRef(0);  // 상세 조회 요청 순번 (늦은 응답 무시용)

  // 새 스케줄 추가 폼
  const [newTask, setNewTask] = useState({
    sports: [] as string[],
    schedule_time: '09:00',
    ai_provider: '',
    crawl_target: 'both',
    auto_publish_psynet: false,
    prediction_only: false,
    notion_admin_id: undefined as number | undefined
  });

  // 수정 모드
  const [editingTasks, setEditingTasks] = useState<{[key: string]: ScheduleTask}>({});
  const [isEditMode, setIsEditMode] = useState(false);

  // 탭별로 분리하여 마운트 시 동일 조회가 중복 실행되지 않도록 함
  useEffect(() => {
    if (subTab === 'manage') {
      loadSchedules();
    }
  }, [subTab, filterAdminId]);

  useEffect(() => {
    if (subTab === 'history') {
      loadHistories(1);
    }
  }, [subTab, historyAdminId]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const data = await getSchedules(isAdministrator ? filterAdminId : undefined);
      setSchedules(data || []);
    } catch (error) {
      console.error('스케줄 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistories = async (page: number = 1) => {
    setLoading(true);
    try {
      const historyData = await getSchedulerHistory(
        undefined,
        page,
        pageSize,
        isAdministrator ? historyAdminId : undefined
      );
      setHistories(historyData.data || []);
      setPagination(historyData.pagination || {
        current_page: 1,
        total_pages: 0,
        page_size: 10,
        total_items: 0,
        has_next: false,
        has_prev: false
      });
      setCurrentPage(page);

      if (page === 1) {
        await getSchedulerStats(undefined, 7);
      }
    } catch (error) {
      console.error('히스토리 조회 실패:', error);
      toast.error('히스토리 조회에 실패했음.');
    } finally {
      setLoading(false);
    }
  };

  const showHistoryDetail = async (historyId: number) => {
    // 이전 요청의 늦은 응답이 현재 모달을 덮어쓰지 않도록 순번 발급
    const requestId = ++detailRequestIdRef.current;

    setSelectedDetail(null);
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await getSchedulerHistoryDetail(historyId);
      if (requestId !== detailRequestIdRef.current) return;
      setSelectedDetail(response.data);
    } catch (error) {
      if (requestId !== detailRequestIdRef.current) return;
      console.error('내역 상세 조회 실패:', error);
      toast.error('상세 정보를 불러오는데 실패했음.');
      setShowDetailModal(false);
    } finally {
      if (requestId === detailRequestIdRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const closeDetailModal = () => {
    // 진행 중인 요청의 응답을 무효화
    detailRequestIdRef.current += 1;
    setShowDetailModal(false);
    setSelectedDetail(null);
    setDetailLoading(false);
  };

  // 다중 종목(쉼표 구분) 라벨 변환
  const formatSports = (sportName: string | null): string => {
    if (!sportName) return '-';
    return sportName
      .split(',')
      .map(s => getSportLabelByName(s.trim()) || s.trim())
      .join(' → ');
  };

  const formatDateTime = (value: string | null): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString('ko-KR');
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}초`;
    return `${Math.floor(seconds / 60)}분 ${seconds % 60}초`;
  };

  // 노션 담당자 표시. 미지정으로 상속됐거나 프롬프트 담당자와 같은 값이면 (동일) 표기.
  // administrator가 만든 스케줄은 노션 담당자가 같은 값으로 명시 저장되므로 ID 비교도 필요함
  const formatNotionAdmin = (history: SchedulerHistoryItem): string => {
    if (!history.notion_admin_name) return '-';
    const sameAsPromptAdmin =
      history.notion_admin_inherited ||
      (history.admin_id !== null && history.notion_admin_id === history.admin_id);
    return sameAsPromptAdmin
      ? `${history.notion_admin_name} (동일)`
      : history.notion_admin_name;
  };

  // 경기 데이터 직렬화 (모달 리렌더마다 재계산되지 않도록 캐시).
  // JSON 파싱 실패 시 백엔드가 원본 문자열을 그대로 내려주므로 분기 처리
  const matchDataText = useMemo(() => {
    const data = selectedDetail?.match_data;
    if (!data) return '';
    return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  }, [selectedDetail?.match_data]);

  const handleCreateAdminChange = (adminId: number | null) => {
    setCreateAdminId(adminId);
    // 담당 관리자 변경 시 노션 페이지도 동일하게 자동 설정
    if (adminId !== null) {
      setNewTask(prev => ({ ...prev, notion_admin_id: adminId }));
    }
  };

  const handleCreateSchedule = async () => {
    try {
      if (newTask.sports.length === 0) {
        toast.warning('최소 하나의 종목을 선택해.');
        return;
      }
      if (isAdministrator && createAdminId === null) {
        toast.warning('스케줄을 등록할 담당 관리자를 선택해.');
        return;
      }
      if (newTask.prediction_only && (!newTask.ai_provider || newTask.ai_provider.trim() === '')) {
        toast.warning("'예측글만' 옵션 사용 시 AI 모델 선택이 필수임.");
        return;
      }

      const targetAdminId = isAdministrator ? createAdminId : undefined;
      await createSchedule({
        sport: newTask.sports.join(','),
        schedule_time: newTask.schedule_time,
        ai_provider: newTask.ai_provider,
        crawl_target: newTask.crawl_target,
        enabled: true,
        auto_publish_psynet: newTask.auto_publish_psynet,
        prediction_only: newTask.prediction_only,
        notion_admin_id: newTask.notion_admin_id
      }, targetAdminId);
      await loadSchedules();

      const adminName = targetAdminId
        ? adminList.find(a => a.admin_id === targetAdminId)?.display_name || `관리자 ${targetAdminId}`
        : '본인';
      toast.success(`스케줄이 등록되었음. (대상: ${adminName})`);
    } catch (error) {
      console.error('스케줄 생성 실패:', error);
      toast.error('스케줄 등록에 실패했음.');
    }
  };

  const handleSportToggle = (sport: string) => {
    setNewTask(prev => {
      const sports = prev.sports.includes(sport)
        ? prev.sports.filter(s => s !== sport)
        : [...prev.sports, sport];
      return { ...prev, sports };
    });
  };

  const handleEditModeToggle = () => {
    if (!isEditMode) {
      const tasksMap: {[key: string]: ScheduleTask} = {};
      schedules.forEach(task => {
        if (task.task_id) {
          tasksMap[task.task_id] = {...task};
        }
      });
      setEditingTasks(tasksMap);
      setIsEditMode(true);
    } else {
      setEditingTasks({});
      setIsEditMode(false);
    }
  };

  const handleSaveChanges = async () => {
    const updates = Object.values(editingTasks);
    let hasError = false;

    for (const task of updates) {
      if (!task.task_id) continue;

      const originalTask = schedules.find(t => t.task_id === task.task_id);
      if (!originalTask) continue;

      const changes: any = {};
      if (task.ai_provider !== originalTask.ai_provider) changes.ai_provider = task.ai_provider;
      if (task.schedule_time !== originalTask.schedule_time) changes.schedule_time = task.schedule_time;
      if (task.enabled !== originalTask.enabled) changes.enabled = task.enabled;
      if (task.crawl_target !== originalTask.crawl_target) changes.crawl_target = task.crawl_target;
      if (task.sport !== originalTask.sport) changes.sport = task.sport;
      if (task.auto_publish_psynet !== originalTask.auto_publish_psynet) changes.auto_publish_psynet = task.auto_publish_psynet;
      if (task.prediction_only !== originalTask.prediction_only) changes.prediction_only = task.prediction_only;
      if (task.notion_admin_id !== originalTask.notion_admin_id) changes.notion_admin_id = task.notion_admin_id;

      if (Object.keys(changes).length === 0) continue;

      try {
        await updateSchedule(task.task_id, changes);
      } catch (error) {
        console.error(`태스크 ${task.task_id} 업데이트 실패:`, error);
        hasError = true;
      }
    }

    if (hasError) {
      toast.warning('일부 스케줄 수정에 실패했음.');
    } else {
      toast.success('스케줄이 저장되었음.');
    }

    await loadSchedules();
    setEditingTasks({});
    setIsEditMode(false);
  };

  const handleTaskFieldChange = (taskId: string, field: string, value: any) => {
    setEditingTasks(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value
      }
    }));
  };

  const handleDeleteSchedule = async (taskId: string) => {
    if (!window.confirm('정말로 이 스케줄을 삭제하시겠습니까?')) return;

    try {
      await deleteSchedule(taskId);
      toast.success('스케줄이 삭제되었음.');
      await loadSchedules();
    } catch (error: any) {
      toast.error(error.message || '스케줄 삭제에 실패했음.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.subTabContainer}>
        <button
          style={{...styles.subTabButton, ...(subTab === 'manage' ? styles.activeSubTab : {})}}
          onClick={() => setSubTab('manage')}
        >
          스케줄 관리
        </button>
        <button
          style={{...styles.subTabButton, ...(subTab === 'history' ? styles.activeSubTab : {})}}
          onClick={() => setSubTab('history')}
        >
          내역 조회
        </button>
      </div>

      {subTab === 'manage' && (
        <>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>새 스케줄 추가</h3>
            <div style={styles.scheduleForm}>
              {isAdministrator ? (
                <div style={styles.formGroupVertical}>
                  <label style={styles.label}>담당 관리자 (필수)</label>
                  <select
                    style={styles.select}
                    value={createAdminId ?? ''}
                    onChange={(e) => handleCreateAdminChange(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">선택해...</option>
                    {adminList.map(admin => (
                      <option key={admin.admin_id} value={admin.admin_id}>
                        {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={styles.formGroupVertical}>
                  <label style={styles.label}>&nbsp;</label>
                  <div style={styles.helperText}>
                    본인 프롬프트 & 노션 페이지 자동 사용
                  </div>
                </div>
              )}
              <div style={styles.formGroupVertical}>
                <label style={styles.label}>종목 (다중 선택)</label>
                <div style={styles.checkboxRow}>
                  {sportOptions.map(sport => (
                    <label key={sport.value} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={newTask.sports.includes(sport.value)}
                        onChange={() => handleSportToggle(sport.value)}
                        style={styles.checkbox}
                      />
                      {sport.label}
                    </label>
                  ))}
                </div>
                {newTask.sports.length > 0 && (
                  <div style={styles.selectedInfo}>
                    선택됨: {newTask.sports.map(s => getSportLabelByName(s) || s).join(' → ')} (순차 실행)
                  </div>
                )}
              </div>

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>수집 대상</label>
                <select
                  style={styles.select}
                  value={newTask.crawl_target}
                  onChange={(e) => setNewTask({...newTask, crawl_target: e.target.value})}
                >
                  <option value="both">오늘 + 내일</option>
                  <option value="today">오늘만</option>
                  <option value="tomorrow">내일만</option>
                </select>
              </div>

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>AI 모델</label>
                <select
                  style={styles.select}
                  value={newTask.ai_provider}
                  onChange={(e) => setNewTask({...newTask, ai_provider: e.target.value})}
                >
                  <option value="">크롤링만</option>
                  <option value="gemini">Gemini</option>
                  <option value="claude">Claude</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>시간</label>
                <input
                  type="time"
                  value={newTask.schedule_time}
                  onChange={(e) => setNewTask({...newTask, schedule_time: e.target.value})}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>&nbsp;</label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={newTask.auto_publish_psynet}
                    onChange={(e) => setNewTask({...newTask, auto_publish_psynet: e.target.checked})}
                    style={styles.checkbox}
                  />
                  <span>Psynet 자동 발행</span>
                </label>
              </div>

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>&nbsp;</label>
                <label style={styles.checkboxLabel} title="크롤링 없이 DB 데이터로 예측글만 작성 (AI 모델 필수)">
                  <input
                    type="checkbox"
                    checked={newTask.prediction_only}
                    onChange={(e) => setNewTask({...newTask, prediction_only: e.target.checked})}
                    style={styles.checkbox}
                  />
                  <span>예측글만</span>
                  {newTask.prediction_only && (!newTask.ai_provider || newTask.ai_provider.trim() === '') && (
                    <span style={styles.warning}>*AI 모델 필수</span>
                  )}
                </label>
              </div>

              {isAdministrator && adminList.length > 0 && (
                <div style={styles.formGroupVertical}>
                  <label style={styles.label}>노션 페이지</label>
                  <select
                    value={newTask.notion_admin_id || ''}
                    onChange={(e) => setNewTask({...newTask, notion_admin_id: e.target.value ? Number(e.target.value) : undefined})}
                    style={styles.selectSmall}
                  >
                    <option value="">담당자와 동일</option>
                    {adminList.map(admin => (
                      <option key={admin.admin_id} value={admin.admin_id}>
                        {admin.display_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={styles.formGroupVertical}>
                <label style={styles.label}>&nbsp;</label>
                <Button
                  variant="primary"
                  onClick={handleCreateSchedule}
                  disabled={newTask.sports.length === 0}
                >
                  추가
                </Button>
              </div>
            </div>
          </div>

          <div style={styles.listSection}>
            <div style={styles.listHeader}>
              <h3 style={styles.sectionTitleInline}>등록된 스케줄</h3>
              {isAdministrator && adminList.length > 0 && (
                <div style={styles.filterRow}>
                  <label style={styles.filterLabel}>조회 필터</label>
                  <select
                    style={styles.filterSelect}
                    value={filterAdminId ?? ''}
                    onChange={(e) => setFilterAdminId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">전체</option>
                    {adminList.map(admin => (
                      <option key={admin.admin_id} value={admin.admin_id}>
                        {admin.display_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!isEditMode ? (
                <Button variant="secondary" onClick={handleEditModeToggle}>
                  수정
                </Button>
              ) : (
                <div style={styles.buttonGroup}>
                  <Button variant="primary" onClick={handleSaveChanges}>
                    저장
                  </Button>
                  <Button variant="secondary" onClick={handleEditModeToggle}>
                    취소
                  </Button>
                </div>
              )}
            </div>
            {loading ? (
              <p style={styles.loadingText}>로딩 중...</p>
            ) : schedules.length === 0 ? (
              <p style={styles.emptyText}>등록된 스케줄이 없음.</p>
            ) : (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {isAdministrator && filterAdminId === null && <th style={styles.th}>소유자</th>}
                      <th style={styles.th}>종목</th>
                      <th style={styles.th}>수집 대상</th>
                      <th style={styles.th}>AI 모델</th>
                      <th style={styles.th}>프롬프트</th>
                      <th style={styles.th}>노션 페이지</th>
                      <th style={styles.th}>예측글만</th>
                      <th style={styles.th}>Psynet 발행</th>
                      <th style={styles.th}>예약 시간</th>
                      <th style={styles.th}>다음 실행</th>
                      <th style={styles.th}>상태</th>
                      <th style={styles.th}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((task: any, index) => {
                      const editingTask = editingTasks[task.task_id] || task;
                      const currentSports = (editingTask.sport || task.sport || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                      const sportsDisplay = task.sport
                        ? task.sport.split(',').map((s: string) => getSportLabelByName(s.trim()) || s.trim()).join(' → ')
                        : '-';
                      const ownerAdmin = adminList.find(a => a.admin_id === task.admin_id);
                      const ownerName = ownerAdmin ? ownerAdmin.display_name : (task.admin_id ? `ID: ${task.admin_id}` : '-');
                      return (
                        <tr key={task.task_id || index}>
                          {isAdministrator && filterAdminId === null && (
                            <td style={styles.td}>{ownerName}</td>
                          )}
                          <td style={styles.td}>
                            {isEditMode ? (
                              <div style={styles.sportCheckboxGroup}>
                                {sportOptions.map(sport => (
                                  <label key={sport.value} style={styles.sportCheckboxLabel}>
                                    <input
                                      type="checkbox"
                                      checked={currentSports.includes(sport.value)}
                                      onChange={(e) => {
                                        let newSports: string[];
                                        if (e.target.checked) {
                                          newSports = [...currentSports, sport.value];
                                        } else {
                                          newSports = currentSports.filter((s: string) => s !== sport.value);
                                        }
                                        const orderedSports = sportOptions.map(s => s.value).filter(s => newSports.includes(s));
                                        handleTaskFieldChange(task.task_id, 'sport', orderedSports.join(','));
                                      }}
                                      style={styles.sportCheckbox}
                                    />
                                    <span style={styles.sportCheckboxText}>{sport.label}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <span title={task.sport}>{sportsDisplay}</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <select
                                value={editingTask.crawl_target || 'both'}
                                onChange={(e) => handleTaskFieldChange(task.task_id, 'crawl_target', e.target.value)}
                                style={styles.selectCompact}
                              >
                                <option value="both">오늘+내일</option>
                                <option value="today">오늘</option>
                                <option value="tomorrow">내일</option>
                              </select>
                            ) : (
                              ({'today': '오늘', 'tomorrow': '내일', 'both': '오늘+내일'} as Record<string, string>)[task.crawl_target] || '오늘+내일'
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <select
                                value={editingTask.ai_provider || ''}
                                onChange={(e) => handleTaskFieldChange(task.task_id, 'ai_provider', e.target.value)}
                                style={styles.selectCompact}
                              >
                                <option value="">크롤링만</option>
                                <option value="gemini">Gemini</option>
                                <option value="claude">Claude</option>
                                <option value="openai">OpenAI</option>
                              </select>
                            ) : (
                              <span style={{
                                color: task.ai_provider ? colors.gray[800] : colors.gray[500],
                                fontStyle: task.ai_provider ? 'normal' : 'italic'
                              }}>
                                {task.ai_provider || '크롤링만'}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <select
                                value={editingTask.admin_id || task.admin_id || ''}
                                onChange={(e) => handleTaskFieldChange(task.task_id, 'admin_id', e.target.value ? Number(e.target.value) : undefined)}
                                style={styles.selectCompact}
                                title="프롬프트 담당 관리자"
                              >
                                {adminList.map(admin => (
                                  <option key={admin.admin_id} value={admin.admin_id}>
                                    {admin.display_name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span>
                                {adminList.find(a => a.admin_id === task.admin_id)?.display_name || '-'}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <select
                                value={editingTask.notion_admin_id || task.notion_admin_id || ''}
                                onChange={(e) => handleTaskFieldChange(task.task_id, 'notion_admin_id', e.target.value ? Number(e.target.value) : undefined)}
                                style={styles.selectCompact}
                                title="노션 페이지 담당 관리자"
                              >
                                {adminList.map(admin => (
                                  <option key={admin.admin_id} value={admin.admin_id}>
                                    {admin.display_name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span>
                                {adminList.find(a => a.admin_id === task.notion_admin_id)?.display_name || '-'}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <label style={styles.checkboxCentered}>
                                <input
                                  type="checkbox"
                                  checked={editingTask.prediction_only || false}
                                  onChange={(e) => handleTaskFieldChange(task.task_id, 'prediction_only', e.target.checked)}
                                  style={styles.checkbox}
                                />
                              </label>
                            ) : (
                              <span style={{
                                ...styles.status,
                                color: task.prediction_only ? colors.status.warning : colors.gray[500]
                              }}>
                                {task.prediction_only ? 'O' : '-'}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <label style={styles.checkboxCentered}>
                                <input
                                  type="checkbox"
                                  checked={editingTask.auto_publish_psynet || false}
                                  onChange={(e) => handleTaskFieldChange(task.task_id, 'auto_publish_psynet', e.target.checked)}
                                  style={styles.checkbox}
                                />
                              </label>
                            ) : (
                              <span style={{
                                ...styles.status,
                                color: task.auto_publish_psynet ? colors.status.info : colors.gray[500]
                              }}>
                                {task.auto_publish_psynet ? '자동' : '수동'}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <input
                                type="time"
                                value={editingTask.schedule_time}
                                onChange={(e) => handleTaskFieldChange(task.task_id, 'schedule_time', e.target.value)}
                                style={styles.inputCompact}
                              />
                            ) : (
                              task.schedule_time
                            )}
                          </td>
                          <td style={styles.td}>
                            {task.next_run ? new Date(task.next_run).toLocaleString('ko-KR') : '-'}
                          </td>
                          <td style={styles.td}>
                            {isEditMode ? (
                              <Button
                                variant={editingTask.enabled ? 'danger' : 'primary'}
                                size="sm"
                                onClick={() => handleTaskFieldChange(task.task_id, 'enabled', !editingTask.enabled)}
                              >
                                {editingTask.enabled ? '비활성화' : '활성화'}
                              </Button>
                            ) : (
                              <AutoStatusBadge status={task.enabled ? '활성' : '비활성'} />
                            )}
                          </td>
                          <td style={styles.td}>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteSchedule(task.task_id)}
                              title="스케줄 삭제"
                            >
                              삭제
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {subTab === 'history' && (
        <div style={styles.historySection}>
          <div style={styles.historyHeader}>
            {isAdministrator && adminList.length > 0 ? (
              <div style={styles.filterRow}>
                <label style={styles.filterLabel}>담당자 필터</label>
                <select
                  style={styles.filterSelect}
                  value={historyAdminId ?? ''}
                  onChange={(e) => setHistoryAdminId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">전체</option>
                  {adminList.map(admin => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <span />
            )}
            <span style={styles.hintText}>레코드를 클릭하면 상세 정보를 볼 수 있음.</span>
          </div>
          {loading ? (
            <div style={styles.loadingText}>히스토리를 불러오는 중...</div>
          ) : histories.length === 0 ? (
            <div style={styles.emptyText}>실행 이력이 없음.</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>종목</th>
                    <th style={styles.th}>경기</th>
                    <th style={styles.th}>프롬프트</th>
                    <th style={styles.th}>노션</th>
                    <th style={styles.th}>AI 모델</th>
                    <th style={styles.th}>실행 시작</th>
                    <th style={styles.th}>실행 종료</th>
                    <th style={styles.th}>상태</th>
                    <th style={styles.th}>재시도</th>
                    <th style={styles.th}>DB 저장</th>
                    <th style={styles.th}>예측 생성</th>
                    <th style={styles.th}>투수 발표</th>
                  </tr>
                </thead>
                <tbody>
                  {histories.map((history) => (
                    <tr
                      key={history.history_id}
                      style={styles.clickableRow}
                      onClick={() => showHistoryDetail(history.history_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          showHistoryDetail(history.history_id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      title="클릭하여 상세 보기"
                    >
                      <td style={styles.td}>{history.history_id}</td>
                      <td style={styles.td}>{formatSports(history.sport_name)}</td>
                      <td style={styles.td}>
                        {history.home_team && history.away_team
                          ? `${history.home_team} vs ${history.away_team}`
                          : history.game_id || '-'}
                      </td>
                      <td style={styles.td}>{history.admin_name || '-'}</td>
                      <td style={styles.td}>{formatNotionAdmin(history)}</td>
                      <td style={styles.td}>
                        <span style={{
                          color: history.ai_provider ? colors.gray[800] : colors.gray[500],
                          fontStyle: history.ai_provider ? 'normal' : 'italic'
                        }}>
                          {history.ai_provider || '크롤링만'}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDateTime(history.execution_start)}</td>
                      <td style={styles.td}>{formatDateTime(history.execution_end)}</td>
                      <td style={styles.td}>
                        <AutoStatusBadge status={history.status} />
                      </td>
                      <td style={styles.td}>
                        {history.retry_count > 0 ? (
                          <span title={history.retry_reason || ''}>
                            {history.retry_count}회
                          </span>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>
                        {history.db_saved ? '✓' : '-'}
                      </td>
                      <td style={styles.td}>
                        {history.prediction_generated ? '✓' : '-'}
                      </td>
                      <td style={styles.td}>
                        {history.sport_name === 'baseball'
                          ? (history.pitcher_announced ? '✓' : '-')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination.total_pages > 0 && (
            <Pagination
              pagination={{
                currentPage: currentPage,
                totalPages: pagination.total_pages,
                totalItems: pagination.total_items,
                hasNext: pagination.has_next,
                hasPrev: pagination.has_prev
              }}
              onPageChange={loadHistories}
              loading={loading}
              style={{ marginTop: spacing[3] }}
            />
          )}
        </div>
      )}

      <Modal
        isOpen={showDetailModal}
        onClose={closeDetailModal}
        title="스케줄러 실행 상세"
        size="lg"
      >
        {detailLoading || !selectedDetail ? (
          <div style={styles.loadingText}>상세 정보를 불러오는 중...</div>
        ) : (
          <>
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>담당자 정보</h4>
              <div style={styles.detailGrid}>
                <div style={styles.detailRow}>
                  <strong>프롬프트 담당자:</strong> {selectedDetail.admin_name || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>노션 담당자:</strong> {formatNotionAdmin(selectedDetail)}
                </div>
                <div style={styles.detailRow}>
                  <strong>스케줄 ID:</strong> {selectedDetail.task_id ?? '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>예약 시간:</strong> {selectedDetail.schedule_time || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>AI 모델:</strong> {selectedDetail.ai_provider || '크롤링만'}
                </div>
                <div style={styles.detailRow}>
                  <strong>수집 대상:</strong>{' '}
                  {({ today: '오늘', tomorrow: '내일', both: '오늘+내일' } as Record<string, string>)[
                    selectedDetail.crawl_target || ''
                  ] || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>예측글만:</strong> {selectedDetail.prediction_only ? 'O' : '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>Psynet 발행:</strong> {selectedDetail.auto_publish_psynet ? '자동' : '수동'}
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>실행 정보</h4>
              <div style={styles.detailGrid}>
                <div style={styles.detailRow}>
                  <strong>이력 ID:</strong> {selectedDetail.history_id}
                </div>
                <div style={styles.detailRow}>
                  <strong>상태:</strong> <AutoStatusBadge status={selectedDetail.status} />
                </div>
                <div style={styles.detailRow}>
                  <strong>종목:</strong> {formatSports(selectedDetail.sport_name)}
                </div>
                <div style={styles.detailRow}>
                  <strong>경기:</strong>{' '}
                  {selectedDetail.home_team && selectedDetail.away_team
                    ? `${selectedDetail.home_team} vs ${selectedDetail.away_team}`
                    : selectedDetail.game_id || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>경기 일자:</strong> {selectedDetail.match_date || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>경기 ID:</strong> {selectedDetail.game_id || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>실행 시작:</strong> {formatDateTime(selectedDetail.execution_start)}
                </div>
                <div style={styles.detailRow}>
                  <strong>실행 종료:</strong> {formatDateTime(selectedDetail.execution_end)}
                </div>
                <div style={styles.detailRow}>
                  <strong>소요 시간:</strong> {formatDuration(selectedDetail.duration)}
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>실행 통계</h4>
              <div style={styles.statGrid}>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>총 경기</span>
                  <span style={styles.statValue}>{selectedDetail.total_matches}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>DB 저장</span>
                  <span style={{ ...styles.statValue, color: colors.status.success }}>
                    {selectedDetail.matches_saved}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>예측 생성</span>
                  <span style={{ ...styles.statValue, color: colors.status.success }}>
                    {selectedDetail.predictions_created}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>노션 저장</span>
                  <span style={{ ...styles.statValue, color: colors.status.success }}>
                    {selectedDetail.notion_saved}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>실패</span>
                  <span style={{ ...styles.statValue, color: colors.status.error }}>
                    {selectedDetail.failed_count}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>스킵</span>
                  <span style={{ ...styles.statValue, color: colors.status.warning }}>
                    {selectedDetail.skipped_count}
                  </span>
                </div>
              </div>
            </div>

            {(selectedDetail.retry_count > 0 || selectedDetail.retry_reason) && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>재시도</h4>
                <div style={styles.detailGrid}>
                  <div style={styles.detailRow}>
                    <strong>재시도 횟수:</strong> {selectedDetail.retry_count}회
                  </div>
                  <div style={styles.detailRow}>
                    <strong>사유:</strong> {selectedDetail.retry_reason || '-'}
                  </div>
                  <div style={styles.detailRow}>
                    <strong>다음 재시도:</strong> {formatDateTime(selectedDetail.next_retry)}
                  </div>
                </div>
              </div>
            )}

            {selectedDetail.error_message && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>오류 메시지</h4>
                <pre style={styles.codeBlock}>{selectedDetail.error_message}</pre>
              </div>
            )}

            {matchDataText && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>경기 데이터</h4>
                <pre style={styles.codeBlock}>{matchDataText}</pre>
              </div>
            )}

            <Button variant="secondary" onClick={closeDetailModal}>
              닫기
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
    paddingBottom: spacing[10],
  },
  helperText: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    padding: `${spacing[3]} 0`,
  },
  warning: {
    color: '#e74c3c',
    fontSize: typography.fontSize.xs,
    marginLeft: spacing[1],
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  filterLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    whiteSpace: 'nowrap',
  },
  filterSelect: {
    ...commonStyles.select,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
    minWidth: '120px',
  },
  subTabContainer: commonStyles.tabContainer,
  subTabButton: {
    ...commonStyles.tabButton,
    padding: `${spacing[3]} ${spacing[6]}`,
    fontSize: typography.fontSize.base,
  },
  activeSubTab: commonStyles.tabButtonActive,
  section: commonStyles.section,
  sectionTitle: commonStyles.sectionTitle,
  sectionTitleInline: {
    ...commonStyles.sectionTitle,
    marginBottom: 0,
  },
  scheduleForm: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[5],
    alignItems: 'flex-end',
  },
  formGroupVertical: commonStyles.formGroupVertical,
  label: commonStyles.label,
  select: commonStyles.select,
  selectSmall: {
    ...commonStyles.select,
    minWidth: '120px',
    padding: `${spacing[2]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  input: commonStyles.input,
  checkboxRow: {
    display: 'flex',
    gap: spacing[5],
    flexWrap: 'wrap',
    padding: `${spacing[2]} 0`,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: spacing[2],
    fontSize: typography.fontSize.base,
    color: colors.gray[800],
  },
  checkbox: {
    cursor: 'pointer',
  },
  selectedInfo: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    marginTop: spacing[1],
  },
  listSection: {
    marginTop: spacing[7],
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    marginBottom: spacing[5],
  },
  buttonGroup: {
    display: 'flex',
    gap: spacing[3],
  },
  tableContainer: {
    ...commonStyles.tableContainer,
    height: '420px',
    maxHeight: '420px',
    overflow: 'auto',
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '4px',
  },
  table: commonStyles.table,
  th: commonStyles.th,
  td: commonStyles.td,
  loadingText: commonStyles.loadingState,
  emptyText: commonStyles.emptyState,
  historySection: {
    padding: spacing[7],
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[4],
    marginBottom: spacing[5],
  },
  hintText: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  },
  clickableRow: {
    cursor: 'pointer',
  },
  detailSection: {
    marginBottom: spacing[7],
  },
  detailSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
    margin: `0 0 ${spacing[4]} 0`,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: spacing[2],
  },
  detailRow: {
    fontSize: typography.fontSize.base,
    color: colors.gray[700],
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: spacing[3],
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[1],
    padding: spacing[3],
    border: `1px solid ${colors.gray[200]}`,
  },
  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.gray[500],
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
  },
  codeBlock: {
    margin: 0,
    padding: spacing[3],
    maxHeight: '240px',
    overflow: 'auto',
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[200]}`,
    fontSize: typography.fontSize.xs,
    color: colors.gray[800],
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  sportCheckboxGroup: commonStyles.checkboxGroup,
  sportCheckboxLabel: commonStyles.checkboxLabel,
  sportCheckbox: {
    cursor: 'pointer',
  },
  sportCheckboxText: {
    color: colors.gray[800],
  },
  selectCompact: {
    ...commonStyles.select,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
    minWidth: '80px',
  },
  inputCompact: {
    ...commonStyles.input,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  checkboxCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};

export default SchedulerManager;
